import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { find, isNumber } from 'lodash';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { ui, uiA, uiBadge } from 'envy-ui';
import { StatusBar } from '.';

export default function Card({
  data: {
    description,
    onTrack,
    majorDisruption,
    minorDisruption,
    upcoming,
    discontinued,
    completed,
    statusPending,
    planNodeId,
    thumbnail: { image, position: backgroundPosition },
    title,
    progress,
    tags: tagIds = [],
  },
  showCardDescription,
  overlapCardTitle,
  showProgress,
  linkPrefix,
  role = undefined,
  settings,
  settings: { displayLevel, drilldownable, fontSize, showStatusBar, showTags, tags, hideTags },
}) {
  const clickable =
    linkPrefix !== false &&
    ((['1', 1, '2', 2, '3', 3, '4', 4, '5', 5].includes(displayLevel)
      ? Number(displayLevel)
      : {
          ONE: 1,
          TWO: 2,
          THREE: 3,
          FOUR: 4,
          FIVE: 5,
        }[displayLevel]) > 1 ||
      drilldownable);

  return (
    <div role={role} className={`child-thumb${clickable ? ' child-thumb-with-link' : ''}`} style={{ pointerEvents: clickable ? 'auto' : 'none' }}>
      <Link to={`${linkPrefix}/${planNodeId}`} className="thumbnail-link" aria-label={title}>
        <div className="child-thumb-img">
          <div
            className="child-img"
            style={{
              backgroundImage: `url(${image})`,
              backgroundPosition,
            }}
          />
          {clickable ? (
            <div className="child-thumb-img-overlay">
              <div className="child-thumb-img-overlay-text">
                Read More
                <FontAwesomeIcon icon={['fal', 'arrow-right']} />
              </div>
            </div>
          ) : (
            ''
          )}
          {overlapCardTitle === 'overlap' && <span className="child-number">{title}</span>}
          {showProgress && isNumber(progress) ? (
            <>
              <div className="progress-line-wrap" data-tooltip-id={`progressTip-${planNodeId}`} data-tooltip-content={`Progress ${Math.round(progress)} %`}>
                <div className="progress-line" style={{ width: `${progress}%` }} />
              </div>
              <Tooltip id={`progressTip-${planNodeId}`} />
            </>
          ) : null}
        </div>
        {overlapCardTitle === 'bottom' && (
          <span className="child-bottom-title" style={{ fontSize: `${parseInt(fontSize, 10) + 2}px` }}>
            {title}
          </span>
        )}
        {showTags && (tagIds || []).length ? (
          <div {...ui([uiA`f f-wrap f-gap-small w-100% m-y`])}>
            {(tagIds || []).map((tagId) => {
              if (hideTags.includes(tagId)) {
                return '';
              }

              return (
                <span key={`home-page-card-tag-${tagId}`} {...ui([uiBadge`--tag`])} style={{ color: find(tags, { tagId })?.colorCode }}>
                  <span {...ui([uiA`color-default`])}>{find(tags, { tagId })?.tagName}</span>
                </span>
              );
            })}
          </div>
        ) : (
          ''
        )}
        {!!showCardDescription && (
          <div className="child-description">
            <span className="child-description-content pd-custom fr-view">
              <FroalaEditorView model={description} />
            </span>
          </div>
        )}
      </Link>
      {/* StatusBar moved outside of Link to prevent nested interactive elements */}
      {showStatusBar ? (
        <StatusBar
          settings={settings}
          onTrack={onTrack}
          majorDisruption={majorDisruption}
          minorDisruption={minorDisruption}
          upcoming={upcoming}
          discontinued={discontinued}
          completed={completed}
          statusPending={statusPending}
          keyPrefix={planNodeId}
        />
      ) : null}
    </div>
  );
}

Card.propTypes = {
  data: PropTypes.shape({
    description: PropTypes.string,
    onTrack: PropTypes.number,
    majorDisruption: PropTypes.number,
    minorDisruption: PropTypes.number,
    upcoming: PropTypes.number,
    discontinued: PropTypes.number,
    completed: PropTypes.number,
    statusPending: PropTypes.number,
    planNodeId: PropTypes.string,
    thumbnail: PropTypes.shape({
      image: PropTypes.string,
      position: PropTypes.string,
    }),
    title: PropTypes.string,
    progress: PropTypes.number,
    showCardDescription: PropTypes.bool,
    overlapCardTitle: PropTypes.string,
    showProgress: PropTypes.bool,
  }).isRequired,
  linkPrefix: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  settings: PropTypes.shape({
    displayLevel: PropTypes.string,
    drilldownable: PropTypes.bool,
    fontSize: PropTypes.string,
    showStatusBar: PropTypes.bool,
  }).isRequired,
  showCardDescription: PropTypes.bool.isRequired,
  overlapCardTitle: PropTypes.string.isRequired,
  showProgress: PropTypes.bool.isRequired,
  role: PropTypes.string,
};
